/**
 * Servicio para gestión de solicitudes de actualización de perfil.
 * Los miembros pueden editar su perfil, pero los cambios requieren aprobación del director.
 */

import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  getDoc,
  doc,
  updateDoc,
  serverTimestamp,
  onSnapshot,
  Unsubscribe,
  Timestamp,
  writeBatch,
} from "firebase/firestore";
import { db } from "@/lib/firebase";

// ============================================
// TIPOS
// ============================================

export type ProfileUpdateStatus =
  | "pending"
  | "approved"
  | "rejected"
  | "cancelled";

export interface EmergencyContact {
  name: string;
  relationship: string;
  phone: string;
}

export interface ProfileUpdateRequest {
  id: string;
  memberId: string;
  personId: string;
  clubId: string;
  memberName: string;
  memberEmail: string;
  memberPhotoUrl?: string;

  // Datos actuales (para comparación)
  currentDataJson?: string;

  // Datos solicitados (nuevos valores)
  phone?: string;
  address?: string;
  healthInsurance?: string;
  bloodType?: string;
  emergencyContact?: EmergencyContact;

  // Campos sensibles
  firstName?: string;
  lastName?: string;
  birthDate?: Date;
  idDocumentType?: string;
  idDocumentNumber?: string;
  gender?: string;

  message?: string;
  status: ProfileUpdateStatus;
  rejectionReason?: string;
  processedBy?: string;
  processedAt?: Date;
  createdAt: Date;
  updatedAt: Date;
}

// ============================================
// SERVICIO
// ============================================

const COLLECTION = "profileUpdateRequests";

/**
 * Obtiene las solicitudes pendientes de un club.
 */
export async function getClubPendingProfileRequests(
  clubId: string,
): Promise<ProfileUpdateRequest[]> {
  const q = query(
    collection(db, COLLECTION),
    where("clubId", "==", clubId),
    where("status", "==", "pending"),
    orderBy("createdAt", "asc"),
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
    birthDate: (doc.data().birthDate as Timestamp)?.toDate(),
    createdAt: (doc.data().createdAt as Timestamp)?.toDate() || new Date(),
    updatedAt: (doc.data().updatedAt as Timestamp)?.toDate() || new Date(),
    processedAt: (doc.data().processedAt as Timestamp)?.toDate(),
  })) as ProfileUpdateRequest[];
}

/**
 * Suscribe a cambios en las solicitudes pendientes de un club.
 */
export function subscribeToClubPendingProfileRequests(
  clubId: string,
  callback: (requests: ProfileUpdateRequest[]) => void,
): Unsubscribe {
  const q = query(
    collection(db, COLLECTION),
    where("clubId", "==", clubId),
    where("status", "==", "pending"),
    orderBy("createdAt", "asc"),
  );

  return onSnapshot(q, (snapshot) => {
    const requests = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      birthDate: (doc.data().birthDate as Timestamp)?.toDate(),
      createdAt: (doc.data().createdAt as Timestamp)?.toDate() || new Date(),
      updatedAt: (doc.data().updatedAt as Timestamp)?.toDate() || new Date(),
      processedAt: (doc.data().processedAt as Timestamp)?.toDate(),
    })) as ProfileUpdateRequest[];
    callback(requests);
  });
}

/**
 * Obtiene el conteo de solicitudes pendientes.
 */
export async function getPendingProfileRequestCount(
  clubId: string,
): Promise<number> {
  const q = query(
    collection(db, COLLECTION),
    where("clubId", "==", clubId),
    where("status", "==", "pending"),
  );

  const snapshot = await getDocs(q);
  return snapshot.size;
}

/**
 * Aprueba una solicitud y aplica los cambios al perfil.
 */
export async function approveProfileRequest(
  requestId: string,
  processedBy: string,
): Promise<void> {
  const requestRef = doc(db, COLLECTION, requestId);
  const requestSnap = await getDocs(
    query(collection(db, COLLECTION), where("__name__", "==", requestId)),
  );

  if (requestSnap.empty) {
    throw new Error("Solicitud no encontrada");
  }

  const requestData = requestSnap.docs[0].data();

  if (requestData.status !== "pending") {
    throw new Error("Esta solicitud ya fue procesada");
  }

  // Construir actualizaciones para la persona (normalizando enums cross-platform)
  const personUpdates: Record<string, unknown> = {
    updatedAt: serverTimestamp(),
  };

  // Normalizador de idDocumentType (acepta Flutter lowercase + web uppercase)
  const normalizeIdDocType = (type: string): string => {
    const mapping: Record<string, string> = {
      cc: "CC",
      CC: "CC",
      ti: "TI",
      TI: "TI",
      ce: "CE",
      CE: "CE",
      rc: "RC",
      RC: "RC",
      passport: "PASAPORTE",
      pasaporte: "PASAPORTE",
      PASAPORTE: "PASAPORTE",
      ppt: "PPT",
      PPT: "PPT",
    };
    return mapping[type] || "CC";
  };

  // Normalizador de memberType (Flutter: camper/leader, Web: acampante/lider)
  const normalizeMemberType = (type: string): string => {
    const mapping: Record<string, string> = {
      camper: "acampante",
      acampante: "acampante",
      leader: "lider",
      lider: "lider",
      líder: "lider",
    };
    return mapping[type?.toLowerCase()] || "acampante";
  };

  // Normalizador de gender
  const normalizeGender = (g: string): string => {
    if (g === "male" || g === "female") return g;
    if (g === "M" || g === "m") return "male";
    if (g === "F" || g === "f") return "female";
    return "male";
  };

  // Normalizador de bloodType (Flutter .name: "aPositive" → "A+")
  const normalizeBloodType = (type: string): string => {
    const mapping: Record<string, string> = {
      aPositive: "A+",
      aNegative: "A-",
      bPositive: "B+",
      bNegative: "B-",
      abPositive: "AB+",
      abNegative: "AB-",
      oPositive: "O+",
      oNegative: "O-",
      "A+": "A+",
      "A-": "A-",
      "B+": "B+",
      "B-": "B-",
      "AB+": "AB+",
      "AB-": "AB-",
      "O+": "O+",
      "O-": "O-",
    };
    return mapping[type] || type;
  };

  // Normalizar nombre (capitalizar primera letra de cada palabra)
  const normalizeName = (name: string): string =>
    name.trim().replace(/\b\w/g, (c) => c.toUpperCase());

  if (requestData.phone) personUpdates.phone = requestData.phone;
  if (requestData.address) personUpdates.address = requestData.address;
  if (requestData.healthInsurance)
    personUpdates.healthInsurance = requestData.healthInsurance;
  if (requestData.bloodType)
    personUpdates.bloodType = normalizeBloodType(requestData.bloodType);
  if (requestData.emergencyContact)
    personUpdates.emergencyContact = requestData.emergencyContact;
  if (requestData.firstName)
    personUpdates.firstName = normalizeName(requestData.firstName);
  if (requestData.lastName)
    personUpdates.lastName = normalizeName(requestData.lastName);
  if (requestData.birthDate) personUpdates.birthDate = requestData.birthDate;

  // Recomputar fullName si cambia firstName o lastName
  if (requestData.firstName || requestData.lastName) {
    const personSnap = await getDoc(doc(db, "persons", requestData.personId));
    const current = personSnap.data() || {};
    const newFirst =
      (requestData.firstName
        ? normalizeName(requestData.firstName)
        : current.firstName) || "";
    const newLast =
      (requestData.lastName
        ? normalizeName(requestData.lastName)
        : current.lastName) || "";
    personUpdates.fullName = `${newFirst} ${newLast}`.trim();
  }
  if (requestData.idDocumentType)
    personUpdates.idDocumentType = normalizeIdDocType(
      requestData.idDocumentType,
    );
  if (requestData.idDocumentNumber)
    personUpdates.idDocumentNumber = requestData.idDocumentNumber;
  if (requestData.gender)
    personUpdates.gender = normalizeGender(requestData.gender);
  if (requestData.memberType)
    personUpdates.memberType = normalizeMemberType(requestData.memberType);

  // Batch write para actualizar persona y solicitud atómicamente
  const batch = writeBatch(db);

  // Actualizar persona
  const personRef = doc(db, "persons", requestData.personId);
  batch.update(personRef, personUpdates);

  // Marcar solicitud como aprobada
  batch.update(requestRef, {
    status: "approved",
    processedBy,
    processedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  await batch.commit();
}

/**
 * Rechaza una solicitud.
 */
export async function rejectProfileRequest(
  requestId: string,
  processedBy: string,
  rejectionReason?: string,
): Promise<void> {
  const requestRef = doc(db, COLLECTION, requestId);

  await updateDoc(requestRef, {
    status: "rejected",
    processedBy,
    rejectionReason: rejectionReason || null,
    processedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

/**
 * Cancela una solicitud (por el miembro).
 */
export async function cancelProfileRequest(requestId: string): Promise<void> {
  const requestRef = doc(db, COLLECTION, requestId);

  await updateDoc(requestRef, {
    status: "cancelled",
    updatedAt: serverTimestamp(),
  });
}

/**
 * Parsea los datos actuales del JSON almacenado.
 */
export function parseCurrentData(
  request: ProfileUpdateRequest,
): Record<string, unknown> {
  if (!request.currentDataJson) return {};
  try {
    return JSON.parse(request.currentDataJson);
  } catch {
    return {};
  }
}

// Exportar servicio como objeto
export const profileUpdateRequestService = {
  getPending: getClubPendingProfileRequests,
  subscribeToPending: subscribeToClubPendingProfileRequests,
  getPendingCount: getPendingProfileRequestCount,
  approve: approveProfileRequest,
  reject: rejectProfileRequest,
  cancel: cancelProfileRequest,
  parseCurrentData,
};
